<script setup lang="ts">
import type { BlockList } from '@/models'
import { type PropType, ref, computed, onMounted, onUnmounted } from 'vue'
import FlexibleBlock from '../../blockStyles/FlexibleBlock.vue'
import { mainBackgroundClasses } from '@/constants'

const props = defineProps({
  blockList: {
    type: Object as PropType<BlockList>,
    required: true,
  },
  isHeaderBlock: {
    type: Boolean,
    default: false,
  },
})

// Snake layout configuration
const SNAKE_COLS_DESKTOP = 4
const SNAKE_COLS_MOBILE = 2
const MD_BREAKPOINT = 768
const SNAKE_CARD_TOP_OFFSET_PX = 30 // pixels from top of card to line position
const SNAKE_ARROW_POSITION = 0.35 // 0-1, where along the line the arrow sits
const SNAKE_EDGE_INSET = 3 // percentage from edge for vertical connectors

// Reactive state
const snakeCols = ref(SNAKE_COLS_DESKTOP)
const mediaQuery = ref<MediaQueryList | null>(null)
const snakeGridRef = ref<HTMLElement | null>(null)
const resizeObserver = ref<ResizeObserver | null>(null)

// Measured row data (updated by ResizeObserver)
interface RowMeasurement {
  topY: number // percentage from container top to line position
}
const rowMeasurements = ref<RowMeasurement[]>([])
const containerHeight = ref(0)

function updateSnakeCols(e: MediaQueryListEvent | MediaQueryList) {
  snakeCols.value = e.matches ? SNAKE_COLS_DESKTOP : SNAKE_COLS_MOBILE
}

// Measure row heights from the DOM
function measureRows() {
  if (!snakeGridRef.value) return

  const grid = snakeGridRef.value
  const gridRect = grid.getBoundingClientRect()
  const cards = grid.querySelectorAll('.snake-card')
  const cols = snakeCols.value

  containerHeight.value = gridRect.height
  if (containerHeight.value === 0) return

  const measurements: RowMeasurement[] = []
  const numRows = Math.ceil(cards.length / cols)

  for (let row = 0; row < numRows; row++) {
    // Get the first card in this row to measure row position
    const cardIndex = row * cols
    const card = cards[cardIndex] as HTMLElement | undefined
    if (!card) continue

    const cardRect = card.getBoundingClientRect()
    // Calculate Y position as percentage: top of card + offset
    const topY = ((cardRect.top - gridRect.top + SNAKE_CARD_TOP_OFFSET_PX) / gridRect.height) * 100
    measurements.push({ topY })
  }

  rowMeasurements.value = measurements
}

onMounted(() => {
  if (props.blockList.subStyle === 'two') {
    mediaQuery.value = window.matchMedia(`(min-width: ${MD_BREAKPOINT}px)`)
    updateSnakeCols(mediaQuery.value)
    mediaQuery.value.addEventListener('change', updateSnakeCols)

    // Set up ResizeObserver to re-measure when grid resizes
    resizeObserver.value = new ResizeObserver(() => {
      measureRows()
    })

    // Initial measurement after DOM settles
    setTimeout(measureRows, 50)
    onSnakeGridMounted()
  }
})

onUnmounted(() => {
  if (props.blockList.subStyle === 'two') {
    mediaQuery.value?.removeEventListener('change', updateSnakeCols)
    resizeObserver.value?.disconnect()
    window.removeEventListener('resize', measureRows)
  }
})

// Called when grid element mounts
function onSnakeGridMounted() {
  if (snakeGridRef.value && resizeObserver.value) {
    resizeObserver.value.observe(snakeGridRef.value)
  }
}

// Returns CSS order value for snake pattern (reverses direction on odd rows)
function getSnakeOrder(index: number): number {
  const cols = snakeCols.value
  const row = Math.floor(index / cols)
  const col = index % cols
  return row * cols + (row % 2 === 0 ? col : cols - 1 - col)
}

// SVG path data (lines only, no markers)
interface SnakePath {
  d: string
}

// Circle/Arrow positions for HTML rendering (won't distort)
interface SnakeMarker {
  x: number // percentage
  y: number // percentage
  type: 'circle' | 'arrow'
  rotation?: number // degrees for arrow
}

// Generate SVG paths using measured row Y positions and calculated X positions
const snakeConnectorPaths = computed((): SnakePath[] => {
  const total = props.blockList.blocks.length
  const measurements = rowMeasurements.value
  if (total < 2 || measurements.length === 0) return []

  const paths: SnakePath[] = []
  const cols = snakeCols.value
  const colCenters = Array.from({ length: cols }, (_, i) => ((i + 0.5) / cols) * 100)

  for (let i = 0; i < total - 1; i++) {
    const currentRow = Math.floor(i / cols)
    const currentCol = i % cols
    const nextRow = Math.floor((i + 1) / cols)
    const nextCol = (i + 1) % cols

    const isReversedRow = currentRow % 2 === 1
    const visualCol = isReversedRow ? cols - 1 - currentCol : currentCol
    const nextIsReversedRow = nextRow % 2 === 1
    const nextVisualCol = nextIsReversedRow ? cols - 1 - nextCol : nextCol

    const x1 = colCenters[visualCol] ?? 1
    const y1 = measurements[currentRow]?.topY ?? 0
    const x2 = colCenters[nextVisualCol] ?? 1
    const y2 = measurements[nextRow]?.topY ?? 0

    if (currentRow === nextRow) {
      // Horizontal line
      paths.push({ d: `M ${x1} ${y1} L ${x2} ${y2}` })
    } else {
      // L-shaped vertical connector
      const edgeX = isReversedRow ? SNAKE_EDGE_INSET : 100 - SNAKE_EDGE_INSET
      paths.push({ d: `M ${x1} ${y1} L ${edgeX} ${y1} L ${edgeX} ${y2} L ${x2} ${y2}` })
    }
  }

  return paths
})

// Generate marker positions using measured row Y positions
const snakeMarkers = computed((): SnakeMarker[] => {
  const total = props.blockList.blocks.length
  const measurements = rowMeasurements.value
  if (total < 2 || measurements.length === 0) return []

  const markers: SnakeMarker[] = []
  const cols = snakeCols.value
  const colCenters = Array.from({ length: cols }, (_, i) => ((i + 0.5) / cols) * 100)

  for (let i = 0; i < total - 1; i++) {
    const currentRow = Math.floor(i / cols)
    const currentCol = i % cols
    const nextRow = Math.floor((i + 1) / cols)
    const nextCol = (i + 1) % cols

    const isReversedRow = currentRow % 2 === 1
    const visualCol = isReversedRow ? cols - 1 - currentCol : currentCol
    const nextIsReversedRow = nextRow % 2 === 1
    const nextVisualCol = nextIsReversedRow ? cols - 1 - nextCol : nextCol

    const x1 = colCenters[visualCol] ?? 1
    const y1 = measurements[currentRow]?.topY ?? 0
    const x2 = colCenters[nextVisualCol] ?? 1
    const y2 = measurements[nextRow]?.topY ?? 0

    // Circle at start
    markers.push({ x: x1, y: y1, type: 'circle' })

    if (currentRow === nextRow) {
      // Arrow positioned along horizontal line (closer to start)
      const arrowX = x1 + (x2 - x1) * SNAKE_ARROW_POSITION
      const rotation = x2 > x1 ? 0 : 180 // right or left
      markers.push({ x: arrowX, y: y1, type: 'arrow', rotation })
    } else {
      // Arrow positioned along vertical segment (closer to start)
      const edgeX = isReversedRow ? SNAKE_EDGE_INSET : 100 - SNAKE_EDGE_INSET
      const arrowY = y1 + (y2 - y1) * SNAKE_ARROW_POSITION
      markers.push({ x: edgeX, y: arrowY, type: 'arrow', rotation: 90 }) // pointing down
    }
  }

  // Circle at final position
  if (total > 1) {
    const lastIndex = total - 1
    const lastRow = Math.floor(lastIndex / cols)
    const lastCol = lastIndex % cols
    const isReversedRow = lastRow % 2 === 1
    const visualCol = isReversedRow ? cols - 1 - lastCol : lastCol
    const x = colCenters[visualCol] ?? 1
    const y = measurements[lastRow]?.topY ?? 0
    markers.push({ x, y, type: 'circle' })
  }

  return markers
})

if (props.blockList.subStyle === 'two') {
  window.addEventListener('resize', measureRows)
}
</script>

<template>
  <div :class="mainBackgroundClasses(blockList.details.backgroundColour)">
    <FlexibleBlock
      :blockData="blockList.details"
      :isHeaderBlock="isHeaderBlock"
      :customClass="mainBackgroundClasses(blockList.details.backgroundColour) + ' justify-start'"
    />

    <!-- Simple flow (subStyle 'one') -->
    <div v-if="blockList.subStyle === 'one'" class="grid grid-cols-5 mx-8">
      <template v-for="(block, index) in blockList.blocks" :key="index">
        <div
          class="flex flex-col text-center m-2 pt-4 rounded-lg items-center bg-white text-black"
          :class="[index % 2 === 1 ? 'mb-10' : '']"
        >
          <p class="border-4 rounded-full w-8 h-8 flex items-center justify-center">
            {{ index + 1 }}
          </p>
          <FlexibleBlock
            :blockData="block"
            :isHeaderBlock="false"
            :customClass="'grid-style grid-style-three'"
          />
        </div>
      </template>
    </div>

    <!-- Snake layout (subStyle 'two') -->
    <div v-if="blockList.subStyle === 'two'" class="snake-layout px-8 py-12">
      <div class="relative">
        <!-- SVG lines overlay (no markers - they distort) -->
        <svg
          class="absolute inset-0 w-full h-full pointer-events-none z-10"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <path
            v-for="(path, i) in snakeConnectorPaths"
            :key="i"
            :d="path.d"
            fill="none"
            stroke="#1a5c4c"
            stroke-width="0.15"
            stroke-dasharray="0.8,0.4"
          />
        </svg>

        <!-- HTML markers (circles and arrows) - won't distort -->
        <div
          v-for="(marker, i) in snakeMarkers"
          :key="'marker-' + i"
          class="absolute pointer-events-none z-20 -translate-x-1/2 -translate-y-1/2"
          :style="{ left: marker.x + '%', top: marker.y + '%' }"
        >
          <!-- Circle marker -->
          <div
            v-if="marker.type === 'circle'"
            class="w-2 h-2 rounded-full bg-white border border-green"
          />
          <!-- Arrow marker -->
          <svg
            v-else
            class="w-2 h-2"
            viewBox="0 0 10 10"
            :style="{ transform: `rotate(${marker.rotation || 0}deg)` }"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#1a5c4c" />
          </svg>
        </div>

        <!-- Cards grid -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4" ref="snakeGridRef">
          <template v-for="(block, index) in blockList.blocks" :key="index">
            <div class="snake-card relative" :style="{ order: getSnakeOrder(index) }">
              <!-- Card content -->
              <div class="bg-white rounded-2xl p-4 shadow-sm h-full">
                <FlexibleBlock
                  :blockData="block"
                  :isHeaderBlock="false"
                  :customClass="'grid-style grid-style-three mt-5'"
                />
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
